// Must run before Router / @remix-run/route-pattern (KODY-VIDEO-M).
import "./lib/array-at-polyfill";
import { createRoot } from "remix/ui";
import { App } from "./app";
import {
  initErrorReporting,
  reportComponentError,
} from "./lib/error-reporting";
import { sweepExportCache } from "./lib/export/export-cache";
import { onNavigate } from "./router";
import { markBoot } from "./lib/boot-timing";
import {
  installCurrentHost,
  loadInstalls,
  recheckStale,
} from "./lib/installed-destinations";
import { currentHost } from "./config/current-host";

markBoot("module");
initErrorReporting();
// Export temp files and zip scratch can be gigabytes; reclaim anything no
// longer referenced. No export can be running at boot, so this is safe.
void sweepExportCache().catch(() => undefined);

const appEl = document.getElementById("app");
if (!appEl) throw new Error("#app mount point missing");

/** Hide the HTML boot hero on non-home routes (never touch it on home). */
function syncBootHeroRoute(): void {
  const home =
    window.location.pathname === "/" || window.location.pathname === "";
  document.documentElement.dataset.route = home ? "home" : "app";
}

// Styles load before the first SPA paint so we do not flash an unstyled
// tree (that was a large desktop CLS). Vite keeps them out of index.html
// (see lcp-first-paint plugin); the boot hero already painted via inline CSS.
await Promise.all([import("./styles/global.css"), import("./styles/home.css")]);
markBoot("styles");

const root = createRoot(appEl);
root.addEventListener("error", (event) => {
  reportComponentError(event.error);
});
root.render(<App />);

queueMicrotask(() => {
  syncBootHeroRoute();
  onNavigate({ signal: new AbortController().signal }, syncBootHeroRoute);
});

/*
 * Installed destinations, after first paint.
 *
 * The read fills the cache every screen asks synchronously; the re-check then
 * asks the catalogue about anything older than a day, which is the only way a
 * withdrawn code ever stops working. Both are fire-and-forget on purpose —
 * nothing on screen waits for either, and a device that is offline keeps every
 * destination it had.
 */
void loadInstalls()
  .then(() => installCurrentHost(currentHost().key))
  .then(() => recheckStale())
  .catch(() => undefined);

// Fonts after first paint so ~74KB of woff2 never contends with LCP.
// Swallow preload failures (deploy-window / cache races): system fonts are
// fine, and an unhandled rejection here was opening Sentry issues (KODY-VIDEO-J).
const loadFonts = () => {
  void import("./styles/fonts.css").catch(() => undefined);
};
if (document.readyState === "complete") {
  loadFonts();
} else {
  window.addEventListener("load", loadFonts, { once: true });
}
